import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, AlertTriangle, Lock } from 'lucide-react';

const LAST_UPDATED = '24 June 2026';

const SECTIONS = [
  {
    title: 'Introduction',
    blocks: [
      { p: 'AVZ Wholesale ("we", "us", "our") is committed to protecting your privacy and handling your personal information responsibly, lawfully and transparently in accordance with the Protection of Personal Information Act, 4 of 2013 ("POPIA").' },
      { p: 'This Privacy Policy explains what personal information we collect, why we collect it, how we use and protect it, and the rights you have as a data subject under POPIA.' },
    ],
  },
  {
    title: 'Responsible Party & Information Officer',
    blocks: [
      { p: 'AVZ Wholesale is the "Responsible Party" that determines the purpose and means of processing your personal information.' },
      { sub: 'For any privacy-related queries, requests or complaints, contact our Information Officer:' },
      { contact: 'info@avzwholesale.co.za' },
      { list: [
        'Or via your usual WhatsApp / email communication channel with AVZ Wholesale.',
      ] },
    ],
  },
  {
    title: 'Personal Information We Collect',
    blocks: [
      { sub: 'We collect only the information needed to operate the platform and fulfil your orders:' },
      { list: [
        'Identity details — your name and surname.',
        'Contact details — cellphone number and email address.',
        'Order & collection details — items ordered, chosen collection point, and order history.',
        'Payment information — payment method and transaction references. We do NOT store your full card details; card payments are handled securely by our payment processor (Paystack).',
        'Account security data — a securely hashed password (we can never see your actual password).',
        'Technical data — limited cookies/session data required to keep you logged in and secure the site.',
      ] },
    ],
  },
  {
    title: 'Why We Process Your Information (Purpose & Lawful Basis)',
    blocks: [
      { sub: 'In line with POPIA\u2019s purpose-specification principle, we process your information only to:' },
      { list: [
        'Create and manage your account.',
        'Process, fulfil and communicate updates about your orders and collections.',
        'Process payments, refunds and store credit, and keep accurate records.',
        'Provide customer support and respond to your requests.',
        'Comply with our legal, tax and regulatory obligations.',
        'Protect the platform against fraud and unauthorised access.',
      ] },
      { p: 'We rely on the lawful grounds of your consent, the performance of our agreement with you (fulfilling your orders), our legitimate interests, and compliance with the law.' },
    ],
  },
  {
    title: 'Sharing & Disclosure',
    blocks: [
      { p: 'We do not sell your personal information. We only share it where strictly necessary, with operators who process data on our behalf under appropriate confidentiality and security obligations:' },
      { list: [
        'Suppliers / vendors — to prepare and fulfil the items you ordered.',
        'Collection points & couriers — to arrange your collection or delivery.',
        'Payment processors (e.g. Paystack) — to securely process payments and refunds.',
        'Communication providers — to send order notifications via email and WhatsApp.',
      ] },
      { p: 'We may also disclose information where required by law or to protect our legal rights.' },
    ],
  },
  {
    title: 'Cross-Border Transfers',
    blocks: [
      { p: 'Some of our service providers (for example email or messaging infrastructure) may process data outside South Africa. Where this happens, we take reasonable steps to ensure your information receives a level of protection comparable to that required by POPIA.' },
    ],
  },
  {
    title: 'Security Safeguards',
    blocks: [
      { sub: 'We apply appropriate, reasonable technical and organisational measures to protect your information against loss, misuse and unauthorised access, including:' },
      { list: [
        'Encryption of data in transit (HTTPS/TLS) with HSTS enforced.',
        'Secure, HttpOnly session cookies and strong password hashing.',
        'Rate-limiting, account lockout and brute-force protection on login.',
        'Strict access controls — staff access is limited to what is needed to run the service.',
        'Security headers (CSP, anti-clickjacking, MIME-sniffing protection) across the platform.',
      ] },
      { warn: 'While we take every reasonable precaution, no method of transmission or storage is completely secure. We continually review and improve our safeguards.' },
    ],
  },
  {
    title: 'Retention of Information',
    blocks: [
      { p: 'We keep your personal information only for as long as necessary to fulfil the purposes described in this policy, or as required by law (for example, financial and tax record-keeping obligations). When information is no longer needed, it is securely deleted or de-identified.' },
    ],
  },
  {
    title: 'Your Rights as a Data Subject',
    blocks: [
      { sub: 'Under POPIA you have the right to:' },
      { list: [
        'Be notified that your information is being collected and used.',
        'Request access to the personal information we hold about you.',
        'Request correction of inaccurate or outdated information.',
        'Request deletion or destruction of your personal information, subject to legal retention requirements.',
        'Object to the processing of your information in certain circumstances.',
        'Withdraw your consent where processing is based on consent.',
        'Lodge a complaint with the Information Regulator.',
      ] },
      { p: 'You can exercise most of these rights directly from your account, or by contacting our Information Officer. We will respond to verified requests within a reasonable time.' },
    ],
  },
  {
    title: 'Cookies & Sessions',
    blocks: [
      { p: 'We use only essential cookies and local session storage needed to keep you signed in, maintain your shopping cart, and secure the platform. We do not use cookies for third-party advertising or cross-site tracking.' },
    ],
  },
  {
    title: 'Complaints to the Information Regulator',
    blocks: [
      { p: 'If you believe we have not handled your personal information lawfully, you may lodge a complaint with the Information Regulator of South Africa:' },
      { list: [
        'Website: inforegulator.org.za',
        'General enquiries: enquiries@inforegulator.org.za',
        'POPIA complaints: POPIAComplaints@inforegulator.org.za',
      ] },
      { p: 'We would, however, appreciate the chance to address your concerns first — please contact our Information Officer before approaching the Regulator.' },
    ],
  },
  {
    title: 'Changes to This Policy',
    blocks: [
      { p: 'We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. The latest version will always be available on this page, with the "last updated" date shown above.' },
    ],
  },
];

const Block = ({ block }) => {
  if (block.p) return <p className="font-body text-sm sm:text-base text-foreground/70 leading-relaxed mb-3">{block.p}</p>;
  if (block.sub) return <p className="font-body text-sm font-semibold text-foreground/90 mt-4 mb-2">{block.sub}</p>;
  if (block.contact) {
    return (
      <p className="font-body text-sm sm:text-base text-foreground/70 leading-relaxed mb-3">
        Email:{' '}
        <a href={`mailto:${block.contact}`} data-testid="privacy-officer-email" className="text-teal hover:underline font-medium">
          {block.contact}
        </a>
      </p>
    );
  }
  if (block.warn) {
    return (
      <div className="flex items-start gap-2.5 my-3 rounded-xl bg-gold/5 border border-gold/25 p-3.5">
        <AlertTriangle className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" strokeWidth={2} />
        <p className="font-body text-sm text-foreground/80 leading-relaxed">{block.warn}</p>
      </div>
    );
  }
  if (block.list) {
    return (
      <ul className="space-y-1.5 mb-3 ml-1">
        {block.list.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 font-body text-sm sm:text-base text-foreground/70 leading-relaxed">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-teal flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
};

export default function PrivacyPage() {
  return (
    <div data-testid="privacy-page" className="bg-background min-h-screen">
      {/* Header */}
      <section className="border-b border-border bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <Link to="/" className="inline-flex items-center gap-1.5 font-body text-xs text-foreground/50 hover:text-teal transition-colors mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 rounded-xl bg-teal/10">
              <Lock className="h-6 w-6 text-teal" strokeWidth={1.75} />
            </div>
            <p className="font-body text-xs sm:text-sm uppercase tracking-[0.3em] text-teal font-bold">
              AVZ Wholesale
            </p>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light text-foreground tracking-tight mb-3">
            Privacy Policy
          </h1>
          <p className="font-body text-sm text-foreground/55 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-teal" /> POPIA compliant · Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="space-y-8 sm:space-y-10">
          {SECTIONS.map((section, idx) => (
            <div key={idx} data-testid={`privacy-section-${idx}`} className="scroll-mt-24">
              <h2 className="font-heading text-xl sm:text-2xl font-medium text-foreground mb-3 flex items-baseline gap-3">
                <span className="font-body text-sm font-bold text-gold">{String(idx + 1).padStart(2, '0')}</span>
                {section.title}
              </h2>
              <div className="pl-0 sm:pl-9">
                {section.blocks.map((b, i) => <Block key={i} block={b} />)}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl bg-card border border-border p-6 sm:p-8 text-center">
          <p className="font-body text-sm text-foreground/60 leading-relaxed">
            Questions about how we handle your information? Contact our Information Officer at{' '}
            <a href="mailto:info@avzwholesale.co.za" className="text-teal hover:underline">info@avzwholesale.co.za</a>.
          </p>
          <Link to="/terms">
            <span className="inline-block mt-4 font-body text-sm font-semibold text-teal hover:text-teal/80 transition-colors">
              View Terms &amp; Conditions →
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
